// =========================================================
// Target.jsx
// ORION BEACH HOTEL · KITE HOUSE
// EXPERIENCIAS DE HUÉSPEDES · REVIEWS
// EDITORIAL · PREMIUM · SOCIAL PROOF
// =========================================================

import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  ArrowRight,
  Check,
  MapPin,
  Quote,
  Star,
  Wind,
} from "lucide-react";

import "../styles/target.css";


// =========================================================
// API
// =========================================================

// =========================================================
// API URL
// DESARROLLO  → Django localhost:8000/api
// PRODUCCIÓN  → https://hotelorionguajira.com/api
// =========================================================

const API_URL = (
  import.meta.env.VITE_API_URL ||
  (
    import.meta.env.DEV
      ? "http://127.0.0.1:8000"
      : "https://hotelorionmayapo.com/"
  )
).replace(/\/+$/, "");


// =========================================================
// WHATSAPP
// =========================================================

const DEFAULT_PHONE = "573104794943";


// =========================================================
// DEFAULT REVIEWS
// =========================================================

const DEFAULT_REVIEWS = [

  {
    id: "review-1",

    name: "Laura Martínez",

    location: "Medellín · Colombia",

    trip: "Escapada de descanso",

    rating: 5,

    title: "Un lugar para desconectarse",

    text:
      "La experiencia fue increíble. El lugar tiene una energía muy especial, la vista al mar es espectacular y todo invita a desconectarse de la rutina. Definitivamente volveríamos.",

    date: "Agosto 2026",

    verified: true,

    initials: "LM",

  },


  {
    id: "review-2",

    name: "Daniel Rodríguez",

    location: "Bogotá · Colombia",

    trip: "Kite Surf",

    rating: 5,

    title: "El lugar perfecto para hacer kite",

    text:
      "Fuimos buscando viento, mar y aventura y encontramos mucho más. La ubicación es excelente y poder combinar alojamiento con kite surf hizo que el viaje fuera increíble.",

    date: "Julio 2026",

    verified: true,

    initials: "DR",

  },


  {
    id: "review-3",

    name: "Sofía Gómez",

    location: "Cali · Colombia",

    trip: "Viaje en pareja",

    rating: 5,

    title: "Una experiencia que queremos repetir",

    text:
      "Pasamos unos días maravillosos. Los atardeceres, la tranquilidad y la cercanía al mar hacen que sea un lugar diferente. Fue exactamente el descanso que necesitábamos.",

    date: "Julio 2026",

    verified: true,

    initials: "SG",

  },


  {
    id: "review-4",

    name: "Andrés Torres",

    location: "Barranquilla · Colombia",

    trip: "Aventura · Caribe",

    rating: 5,

    title: "La Guajira desde otra perspectiva",

    text:
      "Una experiencia auténtica y muy diferente a los hoteles tradicionales. El ambiente es increíble y el contacto con el mar hace que uno realmente sienta que está de vacaciones.",

    date: "Junio 2026",

    verified: true,

    initials: "AT",

  },


  {
    id: "review-5",

    name: "Camila Restrepo",

    location: "Medellín · Colombia",

    trip: "Fin de semana",

    rating: 5,

    title: "Volvería sin pensarlo",

    text:
      "Todo nos encantó. El ambiente, el mar, el viento y la tranquilidad. Es uno de esos lugares que uno descubre y quiere guardar como secreto.",

    date: "Junio 2026",

    verified: true,

    initials: "CR",

  },

];


// =========================================================
// NORMALIZE REVIEWS
// =========================================================

const normalizeReviews = (data) => {

  let items = [];


  if (Array.isArray(data)) {

    items = data;

  }

  else if (Array.isArray(data?.reviews)) {

    items = data.reviews;

  }

  else if (Array.isArray(data?.items)) {

    items = data.items;

  }

  else if (Array.isArray(data?.results)) {

    items = data.results;

  }

  else if (
    Array.isArray(
      data?.content?.reviews
    )
  ) {

    items =
      data.content.reviews;

  }


  return items
    .map(
      (
        item,
        index
      ) => {

        const name =
          item?.name ||
          item?.author ||
          item?.guest ||
          `Huésped ${index + 1}`;


        const initials =
          item?.initials ||
          name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map(
              (word) =>
                word.charAt(0)
                  .toUpperCase()
            )
            .join("");


        return {

          ...item,

          id:
            item?.id ||
            `review-${index}`,

          name,

          location:
            item?.location ||
            item?.city ||
            item?.country ||
            "Colombia",

          trip:
            item?.trip ||
            item?.type ||
            item?.category ||
            "Experiencia Orión",

          rating:
            Math.min(
              5,
              Math.max(
                1,
                Number(
                  item?.rating ||
                  item?.stars ||
                  5
                )
              )
            ),

          title:
            item?.title ||
            "Una experiencia increíble",

          text:
            item?.text ||
            item?.review ||
            item?.description ||
            item?.comment ||
            "Una experiencia inolvidable en Orión Beach Hotel.",

          date:
            item?.date ||
            item?.created_at ||
            item?.createdAt ||
            "",

          verified:
            item?.verified !== false,

          initials,

        };

      }
    )
    .filter(
      (review) =>
        Boolean(review.text)
    );

};


// =========================================================
// STAR RATING
// =========================================================

function Rating({
  rating = 5,
  large = false,
}) {

  return (

    <div
      className={
        large
          ? "review-rating review-rating--large"
          : "review-rating"
      }
      aria-label={
        `${rating} de 5 estrellas`
      }
    >

      {Array.from(
        { length: 5 }
      ).map(
        (_, index) => (

          <Star
            key={index}
            size={
              large
                ? 16
                : 12
            }
            strokeWidth={1.5}
            fill={
              index < rating
                ? "currentColor"
                : "none"
            }
          />

        )
      )}

    </div>

  );

}


// =========================================================
// COMPONENT
// =========================================================

export default function Target() {

  const [
    reviews,
    setReviews
  ] = useState(
    DEFAULT_REVIEWS
  );


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    activeIndex,
    setActiveIndex
  ] = useState(0);


  const [
    isPaused,
    setIsPaused
  ] = useState(false);


  // =======================================================
  // LOAD REVIEWS
  // =======================================================

  useEffect(() => {

    let mounted = true;


    const loadReviews =
      async () => {

        try {

          setLoading(true);


          const response =
            await fetch(
              `${API_URL}/api/content/reviews`
            );


          if (
            !response.ok
          ) {

            throw new Error(
              `HTTP ${response.status}`
            );

          }


          const data =
            await response.json();


          const apiReviews =
            normalizeReviews(
              data
            );


          if (
            mounted &&
            apiReviews.length > 0
          ) {

            setReviews(
              apiReviews
            );

          }

        }

        catch (error) {

          console.warn(
            "API de reseñas no disponible. Utilizando reseñas locales.",
            error
          );

        }

        finally {

          if (mounted) {

            setLoading(false);

          }

        }

      };


    loadReviews();


    return () => {

      mounted = false;

    };

  }, []);


  // =======================================================
  // ACTIVE REVIEW
  // =======================================================

  const activeReview =
    reviews[
      activeIndex
    ] || reviews[0];


  // =======================================================
  // TOTAL RATING
  // =======================================================

  const averageRating =
    useMemo(() => {

      if (
        !reviews.length
      ) {

        return 5;

      }


      const total =
        reviews.reduce(
          (
            sum,
            review
          ) =>
            sum +
            Number(
              review.rating || 5
            ),
          0
        );


      return (
        total /
        reviews.length
      ).toFixed(1);

    }, [
      reviews
    ]);


  // =======================================================
  // NEXT
  // =======================================================

  const nextReview =
    () => {

      if (
        reviews.length <= 1
      ) {

        return;

      }


      setActiveIndex(
        (current) =>
          (
            current + 1
          ) %
          reviews.length
      );

    };


  // =======================================================
  // PREVIOUS
  // =======================================================

  const previousReview =
    () => {

      if (
        reviews.length <= 1
      ) {

        return;

      }


      setActiveIndex(
        (current) =>
          (
            current -
            1 +
            reviews.length
          ) %
          reviews.length
      );

    };


  // =======================================================
  // AUTO PLAY
  // =======================================================

  useEffect(() => {

    if (
      isPaused ||
      reviews.length <= 1
    ) {

      return undefined;

    }


    const interval =
      window.setInterval(
        () => {

          setActiveIndex(
            (current) =>
              (
                current + 1
              ) %
              reviews.length
          );

        },
        6500
      );


    return () => {

      window.clearInterval(
        interval
      );

    };

  }, [
    isPaused,
    reviews.length
  ]);


  // =======================================================
  // KEYBOARD
  // =======================================================

  useEffect(() => {

    const handleKeyDown =
      (event) => {

        if (
          event.key ===
          "ArrowRight"
        ) {

          nextReview();

        }


        if (
          event.key ===
          "ArrowLeft"
        ) {

          previousReview();

        }

      };


    window.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

    };

  });


  // =======================================================
  // WHATSAPP
  // =======================================================

  const whatsappUrl =
    `https://wa.me/${DEFAULT_PHONE}` +
    `?text=${encodeURIComponent(
      "Hola, quiero conocer más sobre la experiencia en Orión Beach Hotel y consultar disponibilidad."
    )}`;


  // =======================================================
  // LOADING
  // =======================================================

  if (
    loading &&
    reviews.length === 0
  ) {

    return (

      <section
        className="target target--loading"
        id="reviews"
      >

        <div className="target__loader">

          <span className="target__spinner" />

          <p>
            Cargando experiencias...
          </p>

        </div>

      </section>

    );

  }


  // =======================================================
  // RENDER
  // =======================================================

  return (

    <section
      className="target"
      id="reviews"
      aria-labelledby="reviews-title"
    >


      {/* =================================================
          DECORATIVE BACKGROUND
      ================================================= */}

      <div
        className="target__ambient target__ambient--top"
        aria-hidden="true"
      />

      <div
        className="target__ambient target__ambient--bottom"
        aria-hidden="true"
      />


      {/* =================================================
          HEADER
      ================================================= */}

      <header className="target__header">

        <div className="target__eyebrow">

          <span className="target__eyebrow-line" />

          <span>
            EXPERIENCIAS REALES
          </span>

          <span className="target__eyebrow-dot">
            ●
          </span>

          <span>
            ORION BEACH HOTEL
          </span>

        </div>


        <div className="target__header-layout">

          <div>

            <h2
              className="target__title"
              id="reviews-title"
            >

              Lo que dicen

              <em>
                nuestros huéspedes.
              </em>

            </h2>


            <p className="target__subtitle">

              Cada visita deja una historia.
              Descubre lo que han vivido
              quienes ya disfrutaron del
              mar, el viento y la magia
              de La Guajira.

            </p>

          </div>


          <div className="target__rating-summary">

            <div className="target__rating-number">
              {averageRating}
            </div>


            <Rating
              rating={5}
              large
            />


            <span>
              EXPERIENCIA DE HUÉSPEDES
            </span>


            <small>
              {reviews.length} reseñas
            </small>

          </div>

        </div>

      </header>


      {/* =================================================
          REVIEW EXPERIENCE
      ================================================= */}

      <div
        className="target__experience"
        onMouseEnter={() =>
          setIsPaused(true)
        }
        onMouseLeave={() =>
          setIsPaused(false)
        }
      >


        {/* =================================================
            LEFT DECORATIVE PANEL
        ================================================= */}

        <aside className="target__experience-side">

          <div className="target__side-top">

            <span>
              01
            </span>

            <span>
              EXPERIENCIAS
            </span>

          </div>


          <Quote
            className="target__quote-icon"
            size={48}
            strokeWidth={1}
          />


          <div className="target__side-bottom">

            <span>
              CARIBE
            </span>

            <i />

            <span>
              LA GUAJIRA
            </span>

          </div>

        </aside>


        {/* =================================================
            REVIEW CARD
        ================================================= */}

        {activeReview && (

          <article
            key={
              activeReview.id
            }
            className="target__review"
          >

            <div className="target__review-glow" />


            {/* TOP */}

            <div className="target__review-top">

              <div className="target__review-quote">

                <Quote
                  size={25}
                  strokeWidth={1.4}
                />

              </div>


              <div className="target__review-meta">

                <Rating
                  rating={
                    activeReview.rating
                  }
                />

                <span>
                  {activeReview.date}
                </span>

              </div>

            </div>


            {/* CONTENT */}

            <div className="target__review-content">

              <span className="target__review-label">

                EXPERIENCIA ·{" "}
                {activeReview.trip}

              </span>


              <h3>
                {activeReview.title}
              </h3>


              <blockquote>
                “{activeReview.text}”
              </blockquote>

            </div>


            {/* AUTHOR */}

            <footer className="target__review-author">

              <div className="target__avatar">

                {activeReview.initials}

              </div>


              <div className="target__author-info">

                <strong>
                  {activeReview.name}
                </strong>


                <span>

                  <MapPin size={12} />

                  {activeReview.location}

                </span>

              </div>


              {activeReview.verified && (

                <div className="target__verified">

                  <Check
                    size={11}
                  />

                  <span>
                    HUÉSPED VERIFICADO
                  </span>

                </div>

              )}

            </footer>

          </article>

        )}


        {/* =================================================
            CONTROLS
        ================================================= */}

        <div className="target__controls">

          <div className="target__counter">

            <strong>
              {String(
                activeIndex + 1
              ).padStart(
                2,
                "0"
              )}
            </strong>

            <span>
              /
            </span>

            <span>
              {String(
                reviews.length
              ).padStart(
                2,
                "0"
              )}
            </span>

          </div>


          <div className="target__progress">

            <span
              style={{
                width:
                  `${
                    (
                      (
                        activeIndex + 1
                      ) /
                      reviews.length
                    ) *
                    100
                  }%`,
              }}
            />

          </div>


          <div className="target__arrows">

            <button
              type="button"
              onClick={
                previousReview
              }
              aria-label="Reseña anterior"
            >

              <ArrowLeft
                size={17}
              />

            </button>


            <button
              type="button"
              onClick={
                nextReview
              }
              aria-label="Siguiente reseña"
            >

              <ArrowRight
                size={17}
              />

            </button>

          </div>

        </div>

      </div>


      {/* =================================================
          REVIEW DOTS
      ================================================= */}

      <div
        className="target__dots"
        role="tablist"
        aria-label="Reseñas"
      >

        {reviews.map(
          (
            review,
            index
          ) => (

            <button
              key={
                review.id
              }
              type="button"
              role="tab"
              aria-selected={
                activeIndex ===
                index
              }
              aria-label={
                `Ver reseña de ${review.name}`
              }
              className={
                activeIndex === index
                  ? "target__dot target__dot--active"
                  : "target__dot"
              }
              onClick={() =>
                setActiveIndex(
                  index
                )
              }
            />

          )
        )}

      </div>


      {/* =================================================
          BOTTOM TRUST BAR
      ================================================= */}

      <div className="target__trust">

        <div className="target__trust-item">

          <span className="target__trust-icon">

            <Star
              size={17}
              fill="currentColor"
            />

          </span>

          <div>

            <strong>
              {averageRating}/5
            </strong>

            <span>
              VALORACIÓN
            </span>

          </div>

        </div>


        <div className="target__trust-divider" />


        <div className="target__trust-item">

          <span className="target__trust-icon">

            <Wind
              size={17}
            />

          </span>

          <div>

            <strong>
              Kite House
            </strong>

            <span>
              EXPERIENCIA
            </span>

          </div>

        </div>


        <div className="target__trust-divider" />


        <div className="target__trust-item">

          <span className="target__trust-icon">

            <MapPin
              size={17}
            />

          </span>

          <div>

            <strong>
              Mayapo
            </strong>

            <span>
              LA GUAJIRA
            </span>

          </div>

        </div>

      </div>


      {/* =================================================
          CTA
      ================================================= */}

      <div className="target__cta">

        <div className="target__cta-decoration">

          <span />

          <Wind
            size={17}
          />

          <span />

        </div>


        <div className="target__cta-content">

          <span className="target__cta-badge">

            <span />

            TU PROPIA HISTORIA

          </span>


          <h3>

            Ahora queremos

            <em>
              conocerte a ti.
            </em>

          </h3>


          <p>

            Ven a descubrir el Caribe,
            el viento y la magia de
            La Guajira.

          </p>

        </div>


        <a
          href={
            whatsappUrl
          }
          className="target__cta-button"
          target="_blank"
          rel="noopener noreferrer"
        >

          <span>
            Quiero vivir la experiencia
          </span>

          <ArrowRight
            size={18}
          />

        </a>

      </div>


      {/* =================================================
          FOOTER
      ================================================= */}

      <footer className="target__footer">

        <span>
          LA GUAJIRA
        </span>

        <i />

        <span>
          CARIBE COLOMBIANO
        </span>

        <i />

        <span>
          ORION BEACH HOTEL
        </span>

        <i />

        <span>
          KITE HOUSE
        </span>

      </footer>

    </section>

  );

}